/**
 * Settings › Domains' own API calls.
 *
 * Kept local to this feature rather than added to `@/lib/api`, for the same
 * reason the audit viewer's and the users screen's are: a feature owns the
 * endpoints only it speaks to, so a session's PR touches no file another
 * session also owns.
 *
 * `DomainsBackend` is the seam the demo client plugs into: `domainsBackendFor`
 * hands back the mock's in-memory store when the app is running on the demo
 * client, so the screen is developable and testable with no API at all.
 */

import type { ApiClient } from '@/api/client'
import { ApiError, type ApiErrorBody } from '@/lib/api'

import type { DomainState } from './types'

const BASE = '/api/v1/tenancy/domain'

export interface DomainsBackend {
  /** The current domain and the CNAME target, whatever state onboarding is in. */
  state(): Promise<DomainState>
  /** Claim a hostname and issue its verification token. */
  request(hostname: string): Promise<DomainState>
  /** Re-check the published records; the response says which half is missing. */
  verify(): Promise<DomainState>
  /** Withdraw it. The hostname stops resolving to this organisation. */
  remove(): Promise<DomainState>
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json', ...(init?.headers ?? {}) } : init?.headers,
  })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
    )
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const domainsApi: DomainsBackend = {
  state: () => request<DomainState>(''),
  request: (hostname) => request<DomainState>('', { method: 'PUT', body: JSON.stringify({ hostname }) }),
  verify: () => request<DomainState>('/verify', { method: 'POST' }),
  remove: () => request<DomainState>('', { method: 'DELETE' }),
}

interface WithDomains {
  readonly domains: DomainsBackend
}

function hasDomains(client: ApiClient): client is ApiClient & WithDomains {
  return typeof (client as Partial<WithDomains>).domains === 'object'
}

/** The demo client's in-memory store when there is one, the real API otherwise. */
export function domainsBackendFor(client: ApiClient): DomainsBackend {
  return hasDomains(client) ? client.domains : domainsApi
}
